import { useCallback, useMemo } from 'react';

import round from 'lodash/round';
import pluralize from 'pluralize';

import { Tooltip, Typography, Box, Stack } from '@cast/design-system';
import { useDiscountsQuery } from '@cast/pricing-discount-engine-data-access';
import {
  Commitment,
  Discount,
  DiscountPricingType,
  DiscountTarget,
  NodeDiscount,
  NodePricing,
} from '@cast/types';
import { formatCurrency } from '@cast/utils';

import { useCommitmentsQuery } from 'hooks/queries/commitments';

import { NodeDiscountType } from '../NodeList/types';

const formatDiscount = ({
  type = 'TYPE_UNKNOWN',
  value,
}: {
  value: string;
  type?: DiscountPricingType;
}) => {
  if (!value) {
    return '';
  }

  if (type === 'TYPE_UNKNOWN') {
    return value;
  }

  const numberValue = Math.abs(Number(value));

  if (type === 'TYPE_PERCENTAGE') {
    return `${round(numberValue * 100, 2)}%`;
  }

  return formatCurrency(numberValue, { maximumFractionDigits: 9 });
};

const discountTargetLabel: Record<DiscountTarget, string> = {
  TARGET_CPU: 'CPU',
  TARGET_MEMORY: 'Memory',
  TARGET_INSTANCE: 'Instance',
  TARGET_COMMITMENT: 'Commitment',
  TARGET_CUSTOM_CPU: 'CPU',
  TARGET_CUSTOM_MEMORY: 'Memory',
  TARGET_CUSTOM_EXTENDED_MEMORY: 'Extended Memory',
  TARGET_CUSTOM_INSTANCE: 'Instance',
  TARGET_UNKNOWN: 'Unknown',
};

const CommitmentRow = ({
  commitment,
  nodeDiscount,
}: {
  commitment?: Commitment;
  nodeDiscount: NodeDiscount;
}) => {
  if (!nodeDiscount.amount || !nodeDiscount.commitment) {
    return null;
  }

  return (
    <Typography variant="P12R" component="li">
      {formatCurrency(Number(nodeDiscount.amount), {
        maximumFractionDigits: 3,
      })}{' '}
      {commitment?.name} (
      {commitment?.gcpResourceCudContext || commitment?.gcpFlexCudContext
        ? 'CUD'
        : commitment?.awsSavingsPlanContext ||
          commitment?.azureSavingsPlanContext
        ? 'SP'
        : 'RI'}
      )
    </Typography>
  );
};

const DiscountRow = ({
  discount,
  nodeDiscount,
}: {
  discount?: Discount;
  nodeDiscount: NodeDiscount;
}) => {
  if (!nodeDiscount.amount || !nodeDiscount.discount) {
    return null;
  }

  const pricing = discount?.pricing[0];

  if (!pricing?.value) {
    return null;
  }

  return (
    <Typography variant="P12R" component="li">
      {formatDiscount({ type: pricing.type, value: pricing.value })}{' '}
      {discount?.target && `on ${discountTargetLabel[discount.target]}`}{' '}
      {discount?.name && `(${discount.name})`}
    </Typography>
  );
};

const adjustmentTypes = ['Discounts', 'Surcharges', 'Custom pricing'] as const;
type AdjustmentType = (typeof adjustmentTypes)[number];

type AdjustmentGroupProps = {
  adjustmentType: AdjustmentType;
  discounts: {
    nodeDiscount: NodeDiscount;
    discount: Discount;
  }[];
};

const AdjustmentGroup = ({
  adjustmentType,
  discounts,
}: AdjustmentGroupProps) => {
  return (
    <Stack gap={4}>
      <Typography variant="P12B">
        {discounts.length} {pluralize(adjustmentType, discounts.length)}
      </Typography>
      <Box
        component="ul"
        sx={{
          pl: 18,
          my: 0,
        }}
      >
        {discounts.map(
          ({ nodeDiscount, discount }) =>
            nodeDiscount.discount?.id && (
              <DiscountRow
                key={nodeDiscount.discount?.id}
                nodeDiscount={nodeDiscount}
                discount={discount}
              />
            )
        )}
      </Box>
    </Stack>
  );
};

type Props = {
  discounts: NonNullable<NodePricing['discounts']>;
  children: React.ReactElement;
  testId?: string;
};

export const NodePriceAdjustmentsTooltip = ({
  discounts: nodeDiscounts,
  children,
  testId,
}: Props) => {
  const { commitmentsDiscounts, normalDiscounts } = useMemo(
    () => ({
      commitmentsDiscounts: nodeDiscounts.filter(
        (d) => d.type === NodeDiscountType.COMMITMENT
      ),
      normalDiscounts: nodeDiscounts.filter(
        (d) => d.type === NodeDiscountType.DISCOUNT
      ),
    }),
    [nodeDiscounts]
  );

  const { data: commitments } = useCommitmentsQuery({
    includeUsage: false,
    enabled: commitmentsDiscounts.length > 0,
  });
  const { data: discounts } = useDiscountsQuery({
    enabled: normalDiscounts.length > 0,
  });

  const getCommitmentById = useCallback(
    (id: string) => {
      return commitments?.find((c) => c.id === id);
    },
    [commitments]
  );

  const groupedAdjustments = useMemo(
    () =>
      normalDiscounts.reduce((acc, nodeDiscount) => {
        const id = nodeDiscount.discount?.id;
        if (!id) {
          return acc;
        }
        const discount = discounts?.find((d) => d.id === id);
        if (!discount) {
          return acc;
        }
        const pricing = discount.pricing[0];
        if (!pricing) {
          return acc;
        }
        const type = pricing.type;
        if (!type) {
          return acc;
        }
        const adjustmentType: AdjustmentType =
          pricing.type === DiscountPricingType.FIXED
            ? 'Custom pricing'
            : pricing.type === DiscountPricingType.FLAT &&
              Number(pricing.value) < 0
            ? 'Surcharges'
            : 'Discounts';
        const group = acc[adjustmentType] || [];
        group.push({ nodeDiscount, discount });
        acc[adjustmentType] = group;
        return acc;
      }, {} as Record<string, { discount: Discount; nodeDiscount: NodeDiscount }[]>),
    [normalDiscounts, discounts]
  );

  return (
    <Tooltip
      testId={testId}
      arrow
      placement="bottom-start"
      title={
        <Stack gap={12} px={4}>
          <Box pt={6}>
            <Typography variant="L10M" color="grey.200">
              Active price adjustments
            </Typography>
          </Box>

          {commitmentsDiscounts.length > 0 && (
            <Stack gap={4}>
              <Typography variant="P12B">
                {commitmentsDiscounts.length}{' '}
                {pluralize('Commitment', commitmentsDiscounts.length)}
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 18,
                  my: 0,
                }}
              >
                {commitmentsDiscounts.map(
                  (d) =>
                    d.commitment?.id && (
                      <CommitmentRow
                        key={`${d.commitment.id}-${d.amount}`}
                        nodeDiscount={d}
                        commitment={getCommitmentById(d.commitment?.id)}
                      />
                    )
                )}
              </Box>
            </Stack>
          )}
          {normalDiscounts.length > 0 &&
            adjustmentTypes.map(
              (adjustmentType) =>
                groupedAdjustments[adjustmentType] && (
                  <AdjustmentGroup
                    key={adjustmentType}
                    adjustmentType={adjustmentType}
                    discounts={groupedAdjustments[adjustmentType]}
                  />
                )
            )}
        </Stack>
      }
    >
      {children}
    </Tooltip>
  );
};
